import { useEffect, useState, FocusEvent, ChangeEvent, useCallback, useMemo, ReactNode } from 'react';
import { Field, useField } from 'react-final-form';
import debounce from 'lodash.debounce';
import { connect } from 'react-redux';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { Textarea, ITextareaProps } from 'UIPackage/MainModule/components/Textarea';
import { IInspectionResult } from 'APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { Button } from 'UIPackage/MainModule/components/Button';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { ICommonFormRef } from 'KnmErknmPackage/MainModule/interfaces/ICommonFormRef';
import { NOT_IN_FIAS_TEXT } from 'CorePackage/MainModule/constants/notInFIASText';
import { getDecisionDataCreatedAt } from 'DecisionPackage/MainModule/selectors/decisionSelectors';

import { IAddressAutoCompleteOption } from '../interfaces/IAddressAutoCompleteOption';
import { useKnmAddresses } from '../hooks/useKnmAddresses';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { CheckboxField } from '../../../UIPackage/MainModule/components/CheckboxField';
import styles from './KnmFiasAddressField.module.scss';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import { Checkbox } from '../../../UIPackage/MainModule/components/Checkbox';
import { getDistrictSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmDistrictSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { findRegionSelectOption } from './regions';
import { getKnmDataCreated, getKnmSystemType } from '../selectors/knmSelectors';
import { IKnmAddressFieldFieldsNames } from './KnmAddressField';
import { ISelectObject, KnmObjectAddressModal, labelLocationObject } from './KnmObjectAddressModal';

const INPUT_VALUE_MAX_LENGTH = 2048;

interface IKnmFiasAddressFieldFieldsNamesExtended {
  isErrorFiasValue?: string;
}

export interface IKnmFiasAddressFieldFieldsNames
  extends IKnmAddressFieldFieldsNames,
    IKnmFiasAddressFieldFieldsNamesExtended {}

const defaultFieldNames: IKnmFiasAddressFieldFieldsNames = {
  fiasRegion: 'fiasRegion',
  notInFIAS: 'notInFIAS',
  manualAddress: 'manualAddress',
  region: 'region',
  regionName: 'regionName',
  isErrorFiasValue: 'isErrorFiasValue',
};

interface IAddressFieldFieldProps {
  inputClassName?: string;
  id: string;
  addressFieldPathWithPrefix: string;
  placeholder?: ReactNode;
  resize?: ITextareaProps['resize'];
  type?: 'row-field' | 'deleting-row';
  required?: boolean;
  prefixNameNotInFias?: string;
  prefixNameMatchesObjectAddress?: string;
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  withWarning?: boolean;
  trigger?: boolean;
  onClear?: () => void;
  inspectionResult?: IInspectionResult;
  formRef?: ICommonFormRef;
  knmSystemType?: KnmSystemTypesEnum;
  hideNotInFiasCheckbox?: boolean;
  hideManualAddress?: boolean;
  manualAddressPlaceholder?: ReactNode;
  regionPlaceholder?: ReactNode;
  isRegionTextField?: boolean;
  fieldPaths?: IKnmFiasAddressFieldFieldsNames;
}

interface IAddressFieldFieldStoreProps {
  districtSelectOptions: IKnmDistrictSelectOption[];
  settings: IAdminSetting[];
  created: string | null | undefined;
  isErp: boolean;
  decisionDataCreated?: string | null;
}

const KnmFiasAddressFieldWithoutStore = ({
  type,
  inputClassName,
  id,
  addressFieldPathWithPrefix,
  placeholder,
  resize,
  clearable,
  disabled,
  withWarning,
  trigger,
  prefixNameNotInFias,
  prefixNameMatchesObjectAddress,
  onClear,
  knmSystemType,
  formRef,
  districtSelectOptions,
  hideNotInFiasCheckbox,
  className,
  manualAddressPlaceholder = 'Адрес',
  inspectionResult,
  settings,
  created,
  isErp,
  isRegionTextField,
  regionPlaceholder,
  hideManualAddress,
  fieldPaths,
  decisionDataCreated,
}: IAddressFieldFieldProps & IAddressFieldFieldStoreProps) => {
  const [addressOptions, setAddressOptions] = useState<IAddressAutoCompleteOption[] | undefined>();
  const [isFocus, setIsFocus] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabledFiasRegion, setIsDisabledFiasRegion] = useState(true);
  const [isShowPopupSelectObject, setIsShowPopupSelectObject] = useState(false);
  const [selectedObject, setSelectedObject] = useState<ISelectObject | undefined>(undefined);
  const [isDisabledNotInFiasBecauseSelectedFiasAddress, setIsDisabledNotInFiasBecauseSelectedFiasAddress] = useState(
    false,
  );

  const change = formRef ? formRef?.current.change : () => {};
  const getFieldState = formRef ? formRef?.current.getFieldState : () => {};

  const finalFieldPaths: Required<IKnmFiasAddressFieldFieldsNames> = useMemo(() => {
    return Object.fromEntries(
      Object.entries(defaultFieldNames).map(([key, defaultValue]) => [
        key,
        fieldPaths?.[key as keyof IKnmFiasAddressFieldFieldsNames] ?? `${prefixNameNotInFias}${defaultValue}`,
      ]),
    ) as Required<IKnmFiasAddressFieldFieldsNames>;
  }, [fieldPaths]);

  const notInFiasValue = useField(finalFieldPaths.notInFIAS).input.value;
  const manualAddressValue = useField(finalFieldPaths.manualAddress, { subscription: { value: true } }).input.value;
  const regionValue = useField(finalFieldPaths.region, { subscription: { value: true } }).input.value;
  const fiasRegionValue = useField(finalFieldPaths.fiasRegion, { subscription: { value: true } }).input.value;

  const addressValue = useField(`${addressFieldPathWithPrefix || ''}`, { subscription: { value: true } }).input.value;
  const matchesObjectAddressValue =
    prefixNameMatchesObjectAddress === undefined
      ? undefined
      : useField(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, {
          subscription: { value: true },
        }).input.value;

  const objectsPmField = useField('objectsPm', {
    subscription: { value: true },
    allowNull: true,
  });
  const objectsErknmField = useField('objectsErknm', {
    subscription: { value: true },
    allowNull: true,
  });

  const isUse4123 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_23_STARTS_FROM,
  );

  const isLaterThanHideFields4129 = isLaterThanAdminSetting(
    created || decisionDataCreated,
    settings,
    AdminSettingCodesEnum.SETTING_HIDE_FIELDS_4_1_29,
  );

  const { isSuccess: isAddressesSuccess, getAddresses, addresses } = useKnmAddresses();

  useEffect(() => {
    if (isAddressesSuccess) {
      setIsLoading(false);
      if (addresses.length > 0) {
        setAddressOptions(
          addresses.map(item => ({
            value: item,
            label: item,
          })),
        );
      } else {
        setAddressOptions(undefined);
      }
    }
  }, [addresses, isAddressesSuccess]);

  const findAddress = (value?: string) => {
    if (value && value.length > 2 && value.length <= INPUT_VALUE_MAX_LENGTH) {
      getAddresses(value).finally(() => setIsLoading(false));
    } else {
      setAddressOptions(undefined);
      setIsLoading(false);
    }
  };

  const debouncedGetAddresses = useCallback(debounce(findAddress, 1000), []);

  /**
   * Получение списка объектов контроля
   */
  const objects = useMemo(() => {
    if (knmSystemType === KnmSystemTypesEnum.PM) {
      return Array.isArray(objectsPmField.input.value) && objectsPmField.input.value.length > 0
        ? objectsPmField.input.value
        : [];
    }

    return Array.isArray(objectsErknmField.input.value) && objectsErknmField.input.value.length > 0
      ? objectsErknmField.input.value
      : [];
  }, [objectsPmField.input.value, objectsErknmField.input.value, knmSystemType]);

  const objectsOfControl = useMemo(() => {
    if (!objects.length) return [];

    return objects.map(i => {
      const result: ISelectObject = {};

      if (i.notInFIAS && i.manualAddress) {
        result.region = i.region;
        result.regionName = i.regionName;
        result.manualAddress = i.manualAddress;

        return result;
      }
      result.address = i.address;
      result.fiasRegion = i.fiasRegion;

      return result;
    });
  }, [objects]);

  /**
   * Функция изменения значения пользователем в галочке "Адрес совпадает с адресом объекта"
   */
  useEffect(() => {
    if (
      matchesObjectAddressValue &&
      prefixNameMatchesObjectAddress !== undefined &&
      prefixNameNotInFias !== undefined
    ) {
      change(finalFieldPaths.isErrorFiasValue);
      if (objectsOfControl && objectsOfControl.length === 1) {
        if (objectsOfControl[0].manualAddress) {
          change(finalFieldPaths.manualAddress, objectsOfControl[0].manualAddress);
          change(finalFieldPaths.region, objectsOfControl[0].region);
          change(addressFieldPathWithPrefix, undefined);
          change(finalFieldPaths.fiasRegion, objectsOfControl[0].region);
        } else {
          change(addressFieldPathWithPrefix, objectsOfControl[0].address);
          change(finalFieldPaths.fiasRegion, objectsOfControl[0].fiasRegion);
        }
      } else if (!objectsOfControl?.find(i => i.manualAddress === manualAddressValue) && manualAddressValue) {
        change(finalFieldPaths.manualAddress);
        change(finalFieldPaths.region);
      }
    }
  }, [
    matchesObjectAddressValue,
    prefixNameMatchesObjectAddress,
    objectsOfControl,
    manualAddressValue,
    prefixNameNotInFias,
    addressFieldPathWithPrefix,
    notInFiasValue,
  ]);

  /**
   * Функция изменения выбранного местоположения объекта кнм
   */
  const onChangeSelectedLocationObject = useCallback((object: ISelectObject) => {
    setSelectedObject(object);
    setIsError(false);
  }, []);

  const onAccept = useCallback(() => {
    if (!selectedObject) {
      setIsError(true);

      return null;
    }

    if (selectedObject.manualAddress) {
      change(finalFieldPaths.notInFIAS, true);
      change(finalFieldPaths.manualAddress, selectedObject.manualAddress);
      change(finalFieldPaths.region, selectedObject.region);
      change(finalFieldPaths.regionName, selectedObject.regionName);
      change(finalFieldPaths.fiasRegion);
      change(addressFieldPathWithPrefix);
    } else {
      change(finalFieldPaths.notInFIAS, false);
      change(finalFieldPaths.manualAddress);
      change(finalFieldPaths.region);
      change(finalFieldPaths.regionName);
      change(addressFieldPathWithPrefix, selectedObject.address);
      change(finalFieldPaths.fiasRegion, selectedObject.fiasRegion);
    }
    change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, true);
    onClose();
  }, [prefixNameNotInFias, addressFieldPathWithPrefix, selectedObject, prefixNameMatchesObjectAddress, formRef]);

  const onClose = useCallback(() => {
    setSelectedObject(undefined);
    setIsError(false);
    setIsShowPopupSelectObject(false);
  }, []);

  /**
   * Функция сравнения выбранного местоположения объекта кнм
   */
  const isSelectedLocationObject = useCallback(
    (object: ISelectObject) => {
      const dataObj = {
        region: regionValue,
        manualAddress: manualAddressValue,
        address: addressValue,
        fiasRegion: fiasRegionValue,
      };

      return labelLocationObject(object) === labelLocationObject(selectedObject || dataObj);
    },
    [prefixNameNotInFias, addressFieldPathWithPrefix, regionValue, addressValue, fiasRegionValue, selectedObject],
  );

  /**
   * Функция изменения галочки нет в фаис
   */
  const onChangeNotInFias = () => {
    change(addressFieldPathWithPrefix);
    change(finalFieldPaths.manualAddress);
    change(finalFieldPaths.region);
    change(finalFieldPaths.fiasRegion);

    if (!matchesObjectAddressValue && prefixNameMatchesObjectAddress !== undefined) {
      change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, false);
      change(finalFieldPaths.manualAddress);
      change(finalFieldPaths.region);
    }
  };

  /**
   * Функция открытия модалки с выбором
   */
  const openPopup = useCallback(() => {
    const dataObj = objectsOfControl?.find(obj => isSelectedLocationObject(obj));

    setSelectedObject(dataObj);
    setIsShowPopupSelectObject(true);
  }, [objectsOfControl]);

  /**
   * Функция изменения галочки "Адрес совпадает с адресом объекта"
   */
  const onChangeMatchesObjectAddress = useCallback(() => {
    if (!matchesObjectAddressValue) {
      openPopup();
    } else {
      change(`${prefixNameMatchesObjectAddress || ''}matchesObjectAddress`, false);
    }
  }, [
    openPopup,
    regionValue,
    manualAddressValue,
    addressValue,
    fiasRegionValue,
    formRef,
    prefixNameMatchesObjectAddress,
  ]);

  const errorPlaceText = getFieldState(addressFieldPathWithPrefix)?.error;

  return (
    <div className={`${styles.MainWrapper} ${className}`}>
      <div className={styles.FiasRow}>
        {!isErp && isLaterThanHideFields4129 && (
          <div className={styles.FIASRegion}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {notInFiasValue ? (
              isRegionTextField ? (
                <TextInputField
                  name={finalFieldPaths.regionName}
                  placeholder="Субъект РФ"
                  size="medium"
                  disabled={true}
                />
              ) : (
                <SelectInputField
                  options={districtSelectOptions}
                  id={finalFieldPaths.region}
                  name={finalFieldPaths.region}
                  placeholder={regionPlaceholder || 'Субъект РФ'}
                  size="medium"
                  disabled={disabled || matchesObjectAddressValue}
                />
              )
            ) : isRegionTextField && !notInFiasValue ? (
              <TextInputField
                name={finalFieldPaths.regionName}
                placeholder="Субъект РФ"
                size="medium"
                disabled={true}
              />
            ) : (
              <SelectInputField
                options={districtSelectOptions}
                id={finalFieldPaths.fiasRegion}
                name={finalFieldPaths.fiasRegion}
                placeholder="Субъект РФ"
                size="medium"
                disabled={disabled || !!notInFiasValue || !!matchesObjectAddressValue || isDisabledFiasRegion}
              />
            )}
          </div>
        )}
        <Field id={id} name={addressFieldPathWithPrefix}>
          {({ input, meta }) => {
            function handleAutoCompleteChange(e: ChangeEvent<HTMLTextAreaElement>) {
              const { value } = e.target;

              setIsDisabledNotInFiasBecauseSelectedFiasAddress(false);

              setIsLoading(true);
              if (value.trim().length === 0) {
                change(finalFieldPaths.isErrorFiasValue);
              } else {
                change(finalFieldPaths.isErrorFiasValue, true);
              }
              input.onChange(value);
              debouncedGetAddresses(value);
            }

            function handleAutoCompleteSelect(value: string) {
              return async () => {
                setIsDisabledNotInFiasBecauseSelectedFiasAddress(true);
                change(finalFieldPaths.notInFIAS, false);
                // Адрес выбран из подсказок ФИАС — ошибка «нет в справочнике» неактуальна,
                // даже если субъект РФ по строке не удалось сопоставить.
                change(finalFieldPaths.isErrorFiasValue);
                input.onChange(value);
                findAddress();
                setAddressOptions(undefined);
                const hasFoundRegion = await findRegionSelectOption(value, undefined, districtSelectOptions);

                if (hasFoundRegion) {
                  change(finalFieldPaths.fiasRegion, hasFoundRegion);
                  setIsDisabledFiasRegion(true);
                } else {
                  change(finalFieldPaths.fiasRegion);
                  setIsDisabledFiasRegion(false);
                }
              };
            }

            function handleTextInputBlur(e: FocusEvent<HTMLTextAreaElement>) {
              input.onBlur(e);
              const tagName = (e?.relatedTarget as Element)?.tagName;
              const idElement = (e?.relatedTarget as Element)?.id;

              if (tagName !== 'UL' && idElement !== finalFieldPaths.notInFIAS) {
                if (!isUse4123) {
                  findAddress();
                  setAddressOptions(undefined);
                  setIsFocus(false);
                }
              }
            }

            function onFocus() {
              setIsFocus(true);
            }

            return (
              <AutoComplete
                type={type}
                options={addressOptions}
                noFoundMessage="Значение отсутствует"
                handleAutoCompleteSelect={handleAutoCompleteSelect}
                isActive={isFocus}
              >
                <Textarea
                  {...input}
                  id={input.name}
                  className={inputClassName}
                  placeholder={placeholder}
                  resize={resize}
                  error={notInFiasValue || !meta.touched ? undefined : meta.error}
                  value={input.value}
                  withWarning={withWarning}
                  clearable={clearable}
                  disabled={disabled || !!notInFiasValue || !!matchesObjectAddressValue}
                  onChange={handleAutoCompleteChange}
                  onBlur={handleTextInputBlur}
                  onClear={onClear}
                  onFocus={onFocus}
                  inspectionResult={inspectionResult}
                  triggerCalculateHeight={trigger}
                />
              </AutoComplete>
            );
          }}
        </Field>
      </div>
      {(prefixNameMatchesObjectAddress !== undefined && !isErp) ||
      matchesObjectAddressValue ||
      (isLaterThanHideFields4129 && prefixNameNotInFias !== undefined && !hideNotInFiasCheckbox) ||
      (notInFiasValue && !hideManualAddress) ? (
        <div className={styles.AddressColumn}>
          <div className={styles.CheckboxWrapper}>
            {prefixNameMatchesObjectAddress !== undefined && !isErp && (
              <Checkbox
                theme="system"
                type="checkbox"
                id={`${prefixNameMatchesObjectAddress}matchesObjectAddress`}
                disabled={disabled}
                checked={matchesObjectAddressValue}
                label={'Адрес совпадает с адресом объекта'}
                onChange={onChangeMatchesObjectAddress}
              />
            )}
            {matchesObjectAddressValue && (
              <Button
                className={`shared-btn-add ${styles.ButtonChange}`}
                text="Изменить"
                variant="link"
                color="primary"
                type="button"
                disabled={disabled}
                onClick={openPopup}
              />
            )}
            {isLaterThanHideFields4129 && prefixNameNotInFias !== undefined && !hideNotInFiasCheckbox && (
              <CheckboxField
                theme="system"
                type="checkbox"
                id={finalFieldPaths.notInFIAS}
                name={finalFieldPaths.notInFIAS}
                disabled={
                  disabled ||
                  isDisabledNotInFiasBecauseSelectedFiasAddress ||
                  isLoading ||
                  (errorPlaceText !== NOT_IN_FIAS_TEXT && !notInFiasValue) ||
                  matchesObjectAddressValue ||
                  (!addressValue && !notInFiasValue)
                }
                label={'Иное указание места (нет в ФИАС)'}
                onChangeCallback={onChangeNotInFias}
              />
            )}
          </div>
          {notInFiasValue && !hideManualAddress && (
            <TextInputField
              id={finalFieldPaths.manualAddress}
              name={finalFieldPaths.manualAddress}
              placeholder={manualAddressPlaceholder}
              disabled={disabled || matchesObjectAddressValue}
              validateOnSubmit
            />
          )}
        </div>
      ) : null}
      <KnmObjectAddressModal
        visible={isShowPopupSelectObject}
        isError={isError}
        disabled={disabled}
        manualAddressFieldId={finalFieldPaths.manualAddress}
        objectsOfControl={objectsOfControl}
        isSelected={isSelectedLocationObject}
        onSelect={onChangeSelectedLocationObject}
        onAccept={onAccept}
        onCancel={onClose}
        onClose={() => setIsShowPopupSelectObject(false)}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState): IAddressFieldFieldStoreProps => ({
  districtSelectOptions: getDistrictSelectOptions(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  isErp: getKnmSystemType(state) === KnmSystemTypesEnum.ERP,
  decisionDataCreated: getDecisionDataCreatedAt(state),
});

export const KnmFiasAddressField = connect(mapStateToProps)(KnmFiasAddressFieldWithoutStore);
